import { createReducer } from '@reduxjs/toolkit';
import { arrayEquals } from 'common/utilities/core/array';
import { generateRandomNumber } from 'common/utilities/core/number';
import { deepClone } from 'common/utilities/core/object';
import { isRowSelection } from 'core/api/api.typesGuards';
import type * as I from 'core/api/coreApiClient';
import {
commandGetFailed,
  commandGetSuccess, commandSaveSuccess, commandUpdateFailed, commandUpdateSuccess, replaceCommand,
  startCommand, } from 'core/redux/actions/command/commandActions';
import {
  clearDownloadPlasmidView,
clearProcessingOnBlur,
  downloadPlasmidView,
  expandSection,
  featuresTableSelection,
fetchingSection, hideEmptySections,
loadingPage,   pageLoaded, pageLoadingFailed, pinLeftSidebar,   plasmidViewerSelection,
  sectionLoaded, sectionLoadingFailed,   sectionTableProcessing, showEmptySections, stopProcessingOnBlur, unpinLeftSidebar,
} from 'core/redux/actions/page/pageActions';
import { loggedInUser, sessionLoaded } from 'core/redux/actions/user/userActions';
import type { UserSettingsConfiguration } from 'core/utilities/core.types';
import { findSectionState, replaceSectionState } from 'core/utilities/page/page';
import { getSectionActiveRowSelection, isSectionDataEmpty } from 'core/utilities/section/section';

import type {
GroupsOfReplacedCommands, PageSectionState,   PageState, ReplacedCommands,
SectionIdToSectionState, } from './pageReducer.types';

export const initialState: PageState = {
  sections: {},
  showEmptySections: true,
  loading: false,
  loadingError: false,
  sectionsLoaded: false,
  groupsOfReplacedCommands: {},
};

export const buildPageSectionState = (section: I.Section, prevSectionState?: PageSectionState): PageSectionState => ({
  id: section.id,
  type: section.type,
  label: section.label || (prevSectionState ? prevSectionState.label : undefined),
  icon: section.icon,
  async: prevSectionState ? prevSectionState.async : (section.fetchSectionCommand !== undefined),
  loading: section.fetchSectionCommand !== undefined,
  empty: (
     
    section.fetchSectionCommand !== undefined
      ? undefined
      : (
        prevSectionState?.empty === undefined
          ? isSectionDataEmpty(section)
          : prevSectionState?.empty
      )
  ),
  error: false,
  hide: false,
  selection: getSectionActiveRowSelection(section)
    ? deepClone(getSectionActiveRowSelection(section))
    : undefined,
  sections: section.sections.reduce<SectionIdToSectionState>((acc, subSection) => {
    acc[subSection.id] = buildPageSectionState(subSection);
    return acc;
  }, {}),
});

const pageReducer = () => createReducer(initialState, (builder) => builder

  .addCase(expandSection, (state, action) => (
    { ...state, expandSection: action.payload.sectionId }
  ))

  .addCase(plasmidViewerSelection, (state, action) => ({
    ...state,
    plasmidViewerSelection: {
      id: generateRandomNumber(),
      selectedIds: action.payload.selection || [],
    },
  }))

  .addCase(featuresTableSelection, (state, action) => ({
    ...state,
    featuresTableSelection: {
      id: generateRandomNumber(),
      selection: action.payload.selection,
    },
  }))

  .addCase(downloadPlasmidView, (state, action) => ({
    ...state,
    downloadPlasmidView: action.payload,
  }))

  .addCase(clearDownloadPlasmidView, (state) => ({
    ...state,
    downloadPlasmidView: undefined,
  }))

  .addCase(showEmptySections, (state) => {
    const newSections: SectionIdToSectionState = {};

    Object.keys(state.sections).forEach((sectionId) => {
      newSections[sectionId] = {
        ...state.sections[sectionId],
        toggledVisibility: undefined,
      };
    });

    return {
      ...state,
      showEmptySections: true,
      sections: newSections,
    };
  })

  .addCase(hideEmptySections, (state) => {
    const newSections: SectionIdToSectionState = {};

    Object.keys(state.sections).forEach((sectionId) => {
      newSections[sectionId] = {
        ...state.sections[sectionId],
        toggledVisibility: undefined,
      };
    });

    return {
      ...state,
      showEmptySections: false,
      sections: newSections,
    };
  })

  .addCase(loadingPage, (state) => ({
    ...state,
    overview: undefined,
    loading: true,
    loadingError: false,
    sectionsLoaded: false,
    sections: {},
    groupsOfReplacedCommands: {},
    pageResponseStatus: undefined,
    persistingCommand: undefined,
  }))

  .addCase(startCommand, (state, action) => {
    if (isRowSelection(action.payload.data) && action.payload.context && action.payload.context.sectionId) {
      const { sectionId } = action.payload.context;

      let newSections: SectionIdToSectionState = {
        ...state.sections,
      };

      // Selection can happen within sub-sections
      const section = findSectionState(newSections, sectionId);

      if (section) {
        const newSection: PageSectionState = {
          ...section,
          selection: deepClone(action.payload.data),
        };

        newSections = replaceSectionState(newSections, newSection);
      }

      return {
        ...state,
        sections: newSections,
        persistingCommand: action.payload.command,
      };
    }

    return { ...state, persistingCommand: action.payload.command };
  })

  .addCase(pageLoaded, (state, action) => {
    const newSections: SectionIdToSectionState = {};
    const { page, responseStatus } = action.payload;

    if (page) {
      page.sections.forEach((section) => {
        newSections[section.id] = buildPageSectionState(section);

        // When reloading the page due to open/close all sections, preserve the empty state
        // to have an accurate show/hide empty sections
        if (arrayEquals(page.sections.map((s) => s.id), Object.keys(state.sections))) {
          if (state.sections[section.id].empty !== undefined) {
            newSections[section.id].empty = state.sections[section.id].empty;
          }
        }
      });
    }

    return {
      ...state,
      loading: false,
      pageResponseStatus: responseStatus,
      overview: page ? {
        type: page.type,
        entityAlias: page.entityAlias,
        entityQualifiedId: page.entityQualifiedId,
      } : undefined,
      sections: newSections,
      sectionsLoaded: !Object.values(newSections).some((s) => s.loading),
    };
  })

  .addCase(pageLoadingFailed, (state, action) => (
    {
      ...state, loading: false, loadingError: true, pageResponseStatus: action.payload.errorResponse?.status,
    }
  ))

  .addCase(commandSaveSuccess, (state, action) => {
    if (!action.payload.page) {
      return {
        ...state,
        loading: false,
        sectionsLoaded: true,
      };
    }

    const newSections: SectionIdToSectionState = {};
    action.payload.page.sections.forEach((section) => {
      newSections[section.id] = buildPageSectionState(section);
    });

    return {
      ...state,
      loading: false,
      sections: newSections,
      sectionsLoaded: true,
    };
  })

  .addCase(fetchingSection, (state, action) => {
    let newSections: SectionIdToSectionState = {};

    newSections = { ...state.sections };

    if (action.payload.section) {
      const sectionId = action.payload.section.id;
      newSections[sectionId] = {
        ...buildPageSectionState(action.payload.section, state.sections[sectionId]),
        loading: true,
      };
    }

    return {
      ...state,
      sectionsLoaded: !Object.values(newSections).some((s) => s.loading),
      sections: newSections,
    };
  })

  .addCase(sectionLoaded, (state, action) => {
    let newSections: SectionIdToSectionState = {};

    newSections = { ...state.sections };

    if (action.payload.section) {
      const sectionId = action.payload.section.id;

      newSections[sectionId] = {
        ...buildPageSectionState(action.payload.section, state.sections[sectionId]),
        hide: action.payload.hide,
        toggledVisibility: action.payload.toggledVisibility,
      };
    }

    return {
      ...state,
      sectionsLoaded: !Object.values(newSections).some((s) => s.loading),
      sections: newSections,
    };
  })

  .addCase(sectionLoadingFailed, (state, action) => {
    let newSections: SectionIdToSectionState = {};

    newSections = { ...state.sections };

    if (action.payload.section) {
      const sectionId = action.payload.section.id;
      newSections[sectionId] = {
        ...buildPageSectionState(action.payload.section, state.sections[sectionId]),
        loading: false,
        empty: undefined,
        error: true,
        hide: true,
        selection: undefined,
        sections: {},
      };
    }

    return {
      ...state,
      sectionsLoaded: !Object.values(newSections).some((s) => s.loading),
      sections: newSections,
    };
  })

  .addCase(sectionTableProcessing, (state, action) => {
    const { sectionId, tableProcessing } = action.payload;

    const sectionToUpdate = findSectionState(state.sections, sectionId);

    if (!sectionToUpdate) {
      throw new Error(`sectionTableProcessing reducer: missing section ${sectionId}`);
      return { ...state };
    }

    const updatedSection: PageSectionState = {
      ...sectionToUpdate,
      tableProcessing,
    };

    const newSections = replaceSectionState(deepClone(state.sections), updatedSection);

    return {
      ...state,
      sections: newSections,
    };
  })

  .addCase(replaceCommand, (state, action) => {
    const newGroupsOfReplacedCommands: GroupsOfReplacedCommands = {
      ...state.groupsOfReplacedCommands,
    };
    const { groupId, command, newCommands } = action.payload;
    const existingGroup: ReplacedCommands = newGroupsOfReplacedCommands[groupId];

    // also create new object from existingGroup, as it is an immutable proxy (via immer)
    // -> An immer producer returned a new value *and* modified its draft. Either return a new value *or* modify the draft.
    newGroupsOfReplacedCommands[groupId] = existingGroup ? { ...existingGroup } : {};
    newGroupsOfReplacedCommands[groupId][command.id] = newCommands;

    return {
      ...state,
      groupsOfReplacedCommands: newGroupsOfReplacedCommands,
    };
  })

  .addCase(stopProcessingOnBlur, (state) => (
    { ...state, stopProcessingOnBlur: true }
  ))

  .addCase(clearProcessingOnBlur, (state) => (
    { ...state, stopProcessingOnBlur: undefined }
  ))

  .addCase(pinLeftSidebar, (state) => (
    { ...state, pinnedLeftSidebar: true }
  ))

  .addCase(unpinLeftSidebar, (state) => (
    { ...state, pinnedLeftSidebar: false }
  ))

  .addCase(sessionLoaded, (state, action) => {
    const userSettingsConfiguration
      = action.payload.appState.session?.userSettingsConfiguration as (UserSettingsConfiguration | undefined)
    ;

    return { ...state, pinnedLeftSidebar: userSettingsConfiguration?.recent_items_pinned };
  })

  .addCase(loggedInUser, (state, action) => {
    const userSettingsConfiguration
      = action.payload.appState?.session?.userSettingsConfiguration as (UserSettingsConfiguration | undefined)
    ;

    return { ...state, pinnedLeftSidebar: userSettingsConfiguration?.recent_items_pinned };
  })

  .addCase(commandGetSuccess, (state) => (
    { ...state, persistingCommand: undefined }
  ))

  .addCase(commandGetFailed, (state) => (
    { ...state, persistingCommand: undefined }
  ))

  .addCase(commandUpdateSuccess, (state) => (
    { ...state, persistingCommand: undefined }
  ))

  .addCase(commandUpdateFailed, (state) => (
    { ...state, persistingCommand: undefined }
  )));

export default pageReducer;
